import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, Video, ChevronLeft, ChevronRight, CheckCircle2, Building2, User, Phone } from 'lucide-react';
import { API } from '../App';

// Return today's YYYY-MM-DD in IST — this is the reference "today" for greying out past dates.
const istTodayISO = () => {
  const d = new Date();
  const istOffsetMin = 5 * 60 + 30;
  const localOffsetMin = d.getTimezoneOffset(); // minutes to add to local to get UTC
  const ist = new Date(d.getTime() + (istOffsetMin + localOffsetMin) * 60000);
  return `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, '0')}-${String(ist.getDate()).padStart(2, '0')}`;
};

const istNowHour = () => {
  const d = new Date();
  const istOffsetMin = 5 * 60 + 30;
  const localOffsetMin = d.getTimezoneOffset();
  const ist = new Date(d.getTime() + (istOffsetMin + localOffsetMin) * 60000);
  return ist.getHours();
};

const modeIcon = (kind) => (kind === 'virtual' ? Video : MapPin);

const BookAppointmentPage = () => {
  const navigate = useNavigate();
  const [clientType, setClientType] = useState(null); // 'B2B' | 'B2C'
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null); // {fields:[]}
  const [common, setCommon] = useState({ full_name: '', email: '', phone: '', appointment_mode: '', consent: false });
  const [formResponses, setFormResponses] = useState({});
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [availability, setAvailability] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [monthOffset, setMonthOffset] = useState(0);
  const [confirmed, setConfirmed] = useState(null);
  const detailsRef = useRef(null);

  // Always start at the top when landing on /book. Fixes the mobile glitch where
  // tapping "Book Now" from mid-homepage would preserve scroll position and land
  // the user on the footer instead of the "Book Your Appointment" heading.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to the freshly-revealed details section ONLY after the user picks a
  // client type. Guarded by `!clientType` so it never fires on initial load.
  useEffect(() => {
    if (!clientType) return;
    const id = requestAnimationFrame(() => {
      detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(id);
  }, [clientType]);

  // Load config once
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/appointments/config`);
        setConfig(res.data);
        // Set default mode to first configured mode
        const firstMode = (res.data?.mode_options || [])[0];
        if (firstMode) setCommon(c => ({ ...c, appointment_mode: firstMode.key }));
      } catch (e) {
        toast.error('Could not load booking configuration');
      }
    })();
  }, []);

  // Load form when client type chosen
  useEffect(() => {
    if (!clientType) { setForm(null); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/appointments/forms/${clientType}`);
        setForm(res.data);
        setFormResponses({});
      } catch (e) {
        toast.error('Could not load the form');
      }
    })();
  }, [clientType]);

  // Load availability when date changes
  useEffect(() => {
    if (!selectedDate) return;
    setAvailLoading(true);
    axios.get(`${API}/appointments/availability?date=${selectedDate}`)
      .then(res => setAvailability(res.data))
      .catch(() => toast.error('Could not load slot availability'))
      .finally(() => setAvailLoading(false));
  }, [selectedDate]);

  // Calendar grid for the current month view (past dates disabled using IST)
  const calendarGrid = useMemo(() => {
    if (!config) return null;
    const todayIso = istTodayISO();
    const today = new Date(todayIso + 'T00:00:00');
    const first = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    const label = first.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
    const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const startWeekday = first.getDay(); // 0 = Sun
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + (config.booking_window_days || 60));
    const openDays = new Set(config.open_days || [0, 1, 2, 3, 4, 5]);
    const blocked = new Set(config.blocked_dates || []);
    for (let d = 1; d <= daysInMonth; d++) {
      const dt = new Date(first.getFullYear(), first.getMonth(), d);
      const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      // Mon=0..Sun=6 (backend convention: weekday() Mon=0)
      const backendDow = (dt.getDay() + 6) % 7;
      const isPast = iso < todayIso;
      const inWindow = dt <= maxDate;
      const enabled = !isPast && inWindow && openDays.has(backendDow) && !blocked.has(iso);
      cells.push({ day: d, iso, enabled, isPast });
    }
    return { label, cells };
  }, [config, monthOffset]);

  // Common fields config from server (fallback to defaults if not loaded yet).
  const commonFields = config?.common_fields || {
    full_name: { label: 'Full Name', required: true, hidden: false },
    email: { label: 'Email', required: true, hidden: false },
    phone: { label: 'Phone (with country code)', required: true, hidden: false },
    appointment_mode: { label: 'Appointment Mode', required: true, hidden: false },
  };
  const modeOptions = config?.mode_options || [];
  const selectedMode = modeOptions.find(m => m.key === common.appointment_mode);

  const validate = () => {
    if (!clientType) { toast.error('Please select whether you are B2B or B2C'); return false; }
    if (commonFields.full_name.required && !common.full_name.trim() && !commonFields.full_name.hidden) {
      toast.error(`Please enter your ${commonFields.full_name.label.toLowerCase()}`); return false;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(common.email)) { toast.error('Please enter a valid email'); return false; }
    if (!common.phone.trim() || common.phone.length < 8) { toast.error('Please enter a valid phone number with country code'); return false; }
    if (!common.appointment_mode) { toast.error('Please choose an appointment mode'); return false; }
    for (const f of (form?.fields || [])) {
      if (f.required && !(formResponses[f.label] || '').trim()) {
        toast.error(`Please fill: ${f.label}`);
        return false;
      }
    }
    if (!selectedDate) { toast.error('Please pick a date'); return false; }
    if (!selectedSlot) { toast.error('Please pick a time slot'); return false; }
    if (!common.consent) { toast.error('Please accept the consent to continue'); return false; }
    return true;
  };

  const submitBooking = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        client_type: clientType,
        full_name: common.full_name.trim(),
        email: common.email.trim(),
        phone: common.phone.trim(),
        appointment_mode: common.appointment_mode,
        date: selectedDate,
        time_slot: selectedSlot,
        consent: common.consent,
        form_responses: formResponses,
      };
      const res = await axios.post(`${API}/appointments`, payload);
      setConfirmed({ ...res.data, ...payload });
      // Jump to top so the confirmation card is visible (especially on mobile,
      // where the form's date picker leaves the viewport near the footer).
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => { navigate('/'); }, 8000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not book your appointment');
    } finally {
      setSubmitting(false);
    }
  };

  const activeSlots = availability?.slots || [];
  const todayIso = istTodayISO();
  const currentHour = istNowHour();

  return (
    <div className="min-h-screen bg-accent py-10 px-4" data-testid="book-appointment-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3">— An exclusive invitation —</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-foreground mb-3">Book Your Appointment</h1>
          <p className="text-muted-foreground max-w-xl mx-auto text-sm sm:text-base">
            Reserve a private consultation at Ambika Fashion. Our couture team will guide you personally.
          </p>
        </div>

        <AnimatePresence mode="wait">
          {confirmed ? (
            <motion.div
              key="confirmed"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white border border-border p-10 text-center"
              data-testid="appointment-confirmed"
            >
              <CheckCircle2 size={72} className="text-primary mx-auto mb-4" />
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3">— Congratulations —</p>
              <h2 className="font-heading text-3xl mb-4">Your appointment is confirmed</h2>
              <div className="border border-border p-6 max-w-md mx-auto mb-6 text-left">
                <p className="mb-2"><strong>Name:</strong> {confirmed.full_name}</p>
                <p className="mb-2"><strong>Date:</strong> {confirmed.date}</p>
                <p className="mb-2"><strong>Time:</strong> {confirmed.time_slot} IST</p>
                {selectedMode && (
                  <>
                    <p className="mb-2"><strong>Mode:</strong> {selectedMode.label}</p>
                    {selectedMode.kind === 'in-person' && selectedMode.address && (
                      <p className="mb-2 flex items-start gap-2">
                        <MapPin size={16} className="mt-1 text-primary" />
                        {selectedMode.map_url ? (
                          <a href={selectedMode.map_url} target="_blank" rel="noreferrer" className="text-primary underline hover:text-primary/80" data-testid="confirmed-map-link">
                            {selectedMode.address}
                          </a>
                        ) : (
                          <span>{selectedMode.address}</span>
                        )}
                      </p>
                    )}
                    {selectedMode.kind === 'virtual' && (
                      <p className="mb-2 flex items-start gap-2">
                        <Video size={16} className="mt-1 text-primary" />
                        <span>Our stylist will call you on WhatsApp video at the scheduled time.</span>
                      </p>
                    )}
                  </>
                )}
              </div>
              <p className="text-sm text-muted-foreground mb-6">A confirmation email has been sent to <strong>{confirmed.email}</strong>.</p>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="px-8 py-3 bg-primary text-primary-foreground hover:bg-primary/90 tracking-wider transition-colors"
                data-testid="return-home-btn"
              >
                Return Home
              </button>
              <p className="text-xs text-muted-foreground mt-4">You will be redirected automatically in a few seconds…</p>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Client Type selector (top) */}
              <div className="bg-white border border-border p-5 sm:p-8">
                <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3 sm:mb-4">1. I am booking as</p>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {[
                    { type: 'B2B', title: 'B2B / Trade', icon: Building2, desc: 'Boutiques, retailers, stylists, designers' },
                    { type: 'B2C', title: 'B2C / Individual', icon: User, desc: 'Bridal, personal styling, private consult' },
                  ].map(({ type, title, icon: Icon, desc }) => {
                    const selected = clientType === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setClientType(type)}
                        className={`text-left border p-3 sm:p-5 transition-all group relative ${
                          selected
                            ? 'border-primary bg-primary/10 ring-2 ring-primary shadow-md'
                            : 'border-border hover:border-primary hover:-translate-y-0.5'
                        }`}
                        data-testid={`client-type-${type.toLowerCase()}`}
                      >
                        {selected && (
                          <CheckCircle2
                            size={16}
                            className="absolute top-2 right-2 text-primary"
                            aria-hidden="true"
                          />
                        )}
                        <Icon size={20} className={`mb-2 sm:mb-3 sm:w-[26px] sm:h-[26px] ${selected ? 'text-primary' : 'text-muted-foreground group-hover:text-primary'}`} />
                        <p className={`font-heading text-sm sm:text-lg leading-tight ${selected ? 'text-primary font-semibold' : ''}`}>{title}</p>
                        <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 leading-snug">{desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Details */}
              {clientType && (
                <div ref={detailsRef} className="bg-white border border-border p-6 sm:p-8 space-y-5 scroll-mt-4" data-testid="details-section">
                  <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">2. Your Details</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {!commonFields.full_name.hidden && (
                      <div>
                        <label className="block text-sm mb-2">{commonFields.full_name.label}{commonFields.full_name.required ? ' *' : ''}</label>
                        <input type="text" value={common.full_name} onChange={(e) => setCommon({ ...common, full_name: e.target.value })}
                          className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none" data-testid="input-full-name" />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm mb-2">{commonFields.email.label} *</label>
                      <input type="email" value={common.email} onChange={(e) => setCommon({ ...common, email: e.target.value })}
                        className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none" data-testid="input-email" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm mb-2 flex items-center gap-2"><Phone size={14} /> {commonFields.phone.label} *</label>
                      <input type="tel" value={common.phone} placeholder="+91 98XXXXXXXX" onChange={(e) => setCommon({ ...common, phone: e.target.value })}
                        className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none" data-testid="input-phone" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm mb-2">{commonFields.appointment_mode.label} *</label>
                    <div className={`grid gap-3 ${modeOptions.length > 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'}`}>
                      {modeOptions.map((m) => {
                        const Icon = modeIcon(m.kind);
                        const selected = common.appointment_mode === m.key;
                        return (
                          <button key={m.key} type="button" onClick={() => setCommon({ ...common, appointment_mode: m.key })}
                            className={`px-4 py-3 border text-left flex items-start gap-3 transition-colors ${selected ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary'}`}
                            data-testid={`mode-${m.key}`}>
                            <Icon size={18} className="mt-0.5 flex-shrink-0" />
                            <span>
                              <span className="block text-sm font-medium">{m.label}</span>
                              {m.kind === 'in-person' && m.address && (
                                <span className={`block text-xs mt-1 ${selected ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>{m.address}</span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Client-type specific custom fields */}
                  {(form?.fields || []).map(f => (
                    <div key={f.id}>
                      <label className="block text-sm mb-2">{f.label}{f.required ? ' *' : ''}</label>
                      {f.field_type === 'textarea' ? (
                        <textarea value={formResponses[f.label] || ''} rows={3}
                          onChange={(e) => setFormResponses({ ...formResponses, [f.label]: e.target.value })}
                          className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none" data-testid={`field-${f.id}`} />
                      ) : f.field_type === 'select' ? (
                        <select value={formResponses[f.label] || ''}
                          onChange={(e) => setFormResponses({ ...formResponses, [f.label]: e.target.value })}
                          className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none bg-white" data-testid={`field-${f.id}`}>
                          <option value="">Select…</option>
                          {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input type={f.field_type === 'email' ? 'email' : f.field_type === 'tel' ? 'tel' : 'text'}
                          value={formResponses[f.label] || ''}
                          onChange={(e) => setFormResponses({ ...formResponses, [f.label]: e.target.value })}
                          className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none" data-testid={`field-${f.id}`} />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Date & Time */}
              {clientType && (
                <div className="bg-white border border-border p-6 sm:p-8" data-testid="date-time-section">
                  <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-4">3. Date &amp; Time</p>
                  {/* Calendar */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <button type="button" onClick={() => setMonthOffset(monthOffset - 1)} disabled={monthOffset === 0}
                        className="p-2 border border-border hover:border-primary disabled:opacity-30" data-testid="cal-prev"><ChevronLeft size={16} /></button>
                      <p className="font-heading text-xl">{calendarGrid?.label}</p>
                      <button type="button" onClick={() => setMonthOffset(monthOffset + 1)} className="p-2 border border-border hover:border-primary" data-testid="cal-next"><ChevronRight size={16} /></button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center text-xs text-muted-foreground mb-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="py-2">{d}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {(calendarGrid?.cells || []).map((c, i) => c ? (
                        <button
                          key={i}
                          type="button"
                          disabled={!c.enabled}
                          onClick={() => { setSelectedDate(c.iso); setSelectedSlot(''); }}
                          className={`aspect-square text-sm border transition-all ${
                            selectedDate === c.iso
                              ? 'bg-primary text-primary-foreground border-primary'
                              : c.enabled
                                ? 'border-border hover:border-primary hover:bg-accent'
                                : c.isPast
                                  ? 'border-transparent text-muted-foreground/30 bg-muted/40 cursor-not-allowed line-through'
                                  : 'border-transparent text-muted-foreground/40 cursor-not-allowed'
                          }`}
                          data-testid={c.enabled ? `date-${c.iso}` : undefined}
                        >
                          {c.day}
                        </button>
                      ) : <div key={i} />)}
                    </div>
                  </div>

                  {/* Slots */}
                  {selectedDate && (
                    <div className="border-t border-border pt-6">
                      <p className="text-sm font-medium mb-3 flex items-center gap-2"><Clock size={14} /> Choose a time slot ({selectedDate}{selectedDate === todayIso ? ' — Today' : ''})</p>
                      {availLoading ? (
                        <p className="text-sm text-muted-foreground">Checking availability…</p>
                      ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                          {activeSlots.map(s => {
                            const isPastToday = selectedDate === todayIso && parseInt(s.slot.split(':')[0], 10) <= currentHour;
                            const disabled = !s.available || isPastToday;
                            const label = !s.available && !isPastToday ? 'Booked' : isPastToday ? 'Passed' : null;
                            return (
                              <button
                                key={s.slot}
                                type="button"
                                disabled={disabled}
                                onClick={() => setSelectedSlot(s.slot)}
                                className={`px-3 py-3 border text-sm transition-colors ${
                                  selectedSlot === s.slot
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : disabled
                                      ? 'border-border bg-muted/50 text-muted-foreground/60 cursor-not-allowed'
                                      : 'border-border hover:border-primary'
                                }`}
                                data-testid={`slot-${s.slot}`}
                              >
                                {s.slot}
                                {label && <span className="block text-[10px] mt-0.5">{label}</span>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {config?.waiting_time_notice && (
                    <div className="border-l-4 border-primary bg-primary/5 p-4 mt-6 text-sm">{config.waiting_time_notice}</div>
                  )}
                </div>
              )}

              {/* Confirm */}
              {clientType && (
                <div className="bg-white border border-border p-6 sm:p-8" data-testid="confirm-section">
                  <label className="flex items-start gap-3 mb-5 cursor-pointer">
                    <input type="checkbox" checked={common.consent} onChange={(e) => setCommon({ ...common, consent: e.target.checked })} className="mt-1" data-testid="consent" />
                    <span className="text-sm text-muted-foreground">I agree to be contacted about my appointment and acknowledge the waiting-time note above.</span>
                  </label>
                  <button
                    type="button"
                    onClick={submitBooking}
                    disabled={submitting}
                    className="w-full sm:w-auto px-10 py-4 bg-primary text-primary-foreground hover:bg-primary/90 tracking-wider disabled:opacity-50"
                    data-testid="btn-submit-booking"
                  >
                    {submitting ? 'Booking…' : (
                      <span className="flex items-center gap-2 justify-center">
                        <Calendar size={16} /> Confirm Appointment
                      </span>
                    )}
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BookAppointmentPage;
